import { useState } from "react";
import { Recommendation } from "./api";
import { PerfRow } from "./ui";

declare const process: { env: Record<string, string | undefined> };
const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/pureh/blend`;

export type OptimizeResult = {
  axis: string;
  added: { material: string; pct: number }[];
  newConcentration: number | null;
  baseConcentration: number;
  before: { longevity: number; projection: number; sillage: number };
  after: { longevity: number; projection: number; sillage: number };
  hours_before: string; hours_after: string;
  added_cost_per_bottle: number | null | { bottleMl: number; eg: number | null; sa: number | null; unmapped: string[] };
  costNote: string;
  newWarnings: string[];
  ceilingHit: boolean;
  reached: boolean;
  log: string[];
  disclaimer: string;
};

const AXES = [
  { key: "longevity", label: "+ثبات" },
  { key: "projection", label: "+فوحان" },
  { key: "sillage", label: "+أثر" },
  { key: "balanced", label: "توازن" },
];
const TIERS = [
  { v: 3, label: "tier 3 (~6h)" },
  { v: 4, label: "tier 4 (~8h)" },
  { v: 5, label: "tier 5 (~10h+)" },
];

export default function OptimizerPanel({ rec, applied, onApply, onUndo }: {
  rec: Recommendation;
  applied: boolean;
  onApply: (r: OptimizeResult) => void;
  onUndo: () => void;
}) {
  const [axis, setAxis] = useState("longevity");
  const [tier, setTier] = useState(4);
  const [result, setResult] = useState<OptimizeResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const run = async () => {
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch(`${BASE}/targets/${rec.target.id}/optimize`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ axis, target_tier: tier }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || res.statusText);
      setResult(await res.json());
    } catch (e: any) {
      setErr(String(e.message || e));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="optimizer-panel">
      <div className="bl-eyebrow mb-2">⚙️ اضبط الأداء</div>
      <div className="flex gap-2 flex-wrap items-center">
        {AXES.map((a) => (
          <button key={a.key} data-testid={`opt-axis-${a.key}`}
            className={`bl-tab ${axis === a.key ? "active" : ""}`}
            onClick={() => { setAxis(a.key); setResult(null); }}>
            {a.label}
          </button>
        ))}
        <select className="bl-select" style={{ width: 150 }} value={tier}
          onChange={(e) => { setTier(Number(e.target.value)); setResult(null); }}
          data-testid="opt-tier-select">
          {TIERS.map((t) => <option key={t.v} value={t.v}>🎯 {t.label}</option>)}
        </select>
        <button className="bl-btn primary" onClick={run} disabled={busy} data-testid="opt-run-btn">
          {busy ? "بيحسب…" : "⚙️ اضبط أوتوماتيك"}
        </button>
      </div>
      {err && <div className="bl-sub mt-2" style={{ color: "var(--bl-danger)" }} data-testid="opt-error">{err}</div>}

      {result && (
        <div className="mt-3 space-y-2" data-testid="opt-result">
          <div className="bl-sub">
            أضفت:{" "}
            <span className="bl-en" data-testid="opt-added-list">
              {result.added.length
                ? result.added.map((a) => `${a.material} +${a.pct}%`).join(" · ")
                : "لا شيء (الهدف متحقق بالفعل)"}
            </span>
            {result.newConcentration != null && (
              <span data-testid="opt-new-conc"> · التركيز {result.baseConcentration}% ← {result.newConcentration}%</span>
            )}
          </div>
          <PerfRow label="الثبات" before={result.before.longevity} after={result.after.longevity} />
          <PerfRow label="الفوحان" before={result.before.projection} after={result.after.projection} />
          <PerfRow label="الأثر" before={result.before.sillage} after={result.after.sillage} />
          <div className="bl-sub bl-en">{result.hours_before} → {result.hours_after}</div>
          <div className="bl-sub" data-testid="opt-cost">
            التكلفة الإضافية/زجاجة 50مل:{" "}
            <span style={{ color: "var(--bl-gold)" }} data-testid="opt-cost-values">
              {typeof result.added_cost_per_bottle === "object" && result.added_cost_per_bottle
                ? `${(result.added_cost_per_bottle as any).eg ?? "—"} EGP · ${(result.added_cost_per_bottle as any).sa ?? "—"} SAR`
                : "—"}
            </span>
            <span className="mr-1" style={{ fontSize: 10 }}>({result.costNote})</span>
          </div>
          {result.newWarnings.length > 0 && (
            <div className="flex gap-1 flex-wrap" data-testid="opt-new-warnings">
              {result.newWarnings.map((w, i) => <span key={i} className="bl-pill danger">{w}</span>)}
            </div>
          )}
          {result.ceilingHit && !result.reached && (
            <div className="bl-pill danger" data-testid="opt-ceiling">وصلنا أقصى حد ممكن قبل الهدف (سقف المُعزّزات/التركيز)</div>
          )}
          <div className="flex gap-2">
            <button className="bl-btn primary" onClick={() => onApply(result)}
              disabled={applied || !result.added.length && result.newConcentration == null}
              data-testid="opt-apply-btn">✓ Apply</button>
            <button className="bl-btn ghost" onClick={() => { onUndo(); setResult(null); }}
              disabled={!applied} data-testid="opt-undo-btn">↩︎ Undo</button>
          </div>
          <p className="bl-disclaimer">* {result.disclaimer}</p>
        </div>
      )}
    </div>
  );
}
